<script setup>
import { computed } from 'vue'
import { getMarketplace } from '@/constants/marketplaces'

/**
 * AppMarketplaceBadge — mini logo + marketplace name on one row.
 *
 * Reads from the shared `MARKETPLACES` constants so renaming a label or
 * swapping a logo is a one-place edit. Falls back gracefully for unknown
 * keys (em-dash if neither key nor label is available).
 *
 *   <AppMarketplaceBadge marketplace="ebay" />
 *   <AppMarketplaceBadge marketplace="collx" size="lg" :show-label="false" />
 *
 * Replaces the inline `MARKETPLACE_MAP` pattern that existed in
 * `OrdersMarketplaceCell.vue`, the Orders stat strip, and the Sale Pick
 * List channel strip.
 */
const props = defineProps({
  marketplace: { type: String, required: true },
  size:        { type: String, default: 'md' },   // 'sm' (16px) | 'md' (24px) | 'lg' (28px) | 'xl' (32px)
  showLabel:   { type: Boolean, default: true },
  labelClass:  { type: String, default: 'text-xs font-medium text-gray-900' },
  /**
   * Wrap the logo in a `bg-white rounded-md p-[2px]` square frame so it reads
   * as a "chip" against a non-white background (gray surface cards, dark
   * panels). Used for the Sale Pick List order-group header (Figma node
   * 23743:388491) where the eBay/Shopify/CollX logos sit in white tiles on a
   * gray-50 row.
   */
  framed:      { type: Boolean, default: false },
})

const meta = computed(() => getMarketplace(props.marketplace))

const sizeClass = computed(() => ({
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-7 h-7',
  xl: 'w-8 h-8',
}[props.size] ?? 'w-6 h-6'))
</script>

<template>
  <div class="inline-flex items-center gap-x-1.5">
    <!-- Framed: white tile wrapping the logo. Logo fills via object-contain so
         it adapts to whatever the marketplace's aspect ratio is (eBay is wide,
         Shopify is square). -->
    <span
      v-if="framed"
      class="shrink-0 inline-flex items-center justify-center bg-white rounded-md p-0.5"
      :class="sizeClass"
    >
      <img
        v-if="meta.logo"
        :src="meta.logo"
        :alt="meta.label"
        class="w-full h-full object-contain"
      />
      <span v-else class="text-gray-400 text-[10px]" aria-hidden="true">—</span>
    </span>
    <img
      v-else-if="meta.logo"
      :src="meta.logo"
      :alt="meta.label"
      class="shrink-0"
      :class="sizeClass"
    />
    <!-- No logo (e.g. `other`) → neutral square so the row height stays
         consistent across mixed marketplaces. -->
    <span
      v-else
      class="shrink-0 inline-flex items-center justify-center bg-gray-100 text-gray-400 rounded-sm text-[10px]"
      :class="sizeClass"
      aria-hidden="true"
    >
      —
    </span>
    <span v-if="showLabel" :class="labelClass">{{ meta.label }}</span>
  </div>
</template>
